import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Icon,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
  message
} from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { withAppContext } from 'context/AppContext';
import { buildEInvoiceCouponDetailUri, buildEInvoiceCouponsUri } from 'utils/routes';
import { createVoucher, deleteVoucher, getVoucher, getVoucherProducts, reactivateVoucher, updateVoucher } from 'utils/apis/eInvoiceCoupon';
import { StyledCard, HeaderSection, BodySection } from './eInvoice.styles';
import CouponPageHeader from './components/CouponPageHeader';

const { Option } = Select;

const NAVIGATION_KEYS = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'];

const blockIntegerKeyDown = e => {
  if (NAVIGATION_KEYS.indexOf(e.key) !== -1 || e.ctrlKey || e.metaKey) {
    return;
  }
  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

const blockDecimalKeyDown = e => {
  if (NAVIGATION_KEYS.indexOf(e.key) !== -1 || e.ctrlKey || e.metaKey) {
    return;
  }
  if (/^\d$/.test(e.key)) {
    return;
  }
  if (e.key === '.' && String(e.target.value).indexOf('.') === -1) {
    return;
  }
  e.preventDefault();
};

const parseIntegerInput = value => {
  if (value == null || value === '') {
    return '';
  }
  return String(value).replace(/[^\d]/g, '');
};

const parseDecimalInput = value => {
  if (value == null || value === '') {
    return '';
  }
  const cleaned = String(value).replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 1) {
    return cleaned;
  }
  return `${parts[0]}.${parts.slice(1).join('')}`;
};

const optionalPositiveIntegerRules = label => [
  {
    validator: (rule, value, callback) => {
      if (value == null || value === '') {
        callback();
        return;
      }
      if (!Number.isFinite(value) || !Number.isInteger(value) || value < 1) {
        callback(`${label} must be a whole number of at least 1`);
        return;
      }
      callback();
    }
  }
];

const optionalPositiveDecimalRules = label => [
  {
    validator: (rule, value, callback) => {
      if (value == null || value === '') {
        callback();
        return;
      }
      if (!Number.isFinite(value) || value < 0) {
        callback(`${label} must be 0 or greater`);
        return;
      }
      callback();
    }
  }
];

const requiredPositiveIntegerRules = label => [
  { required: true, message: `${label} is required` },
  {
    validator: (rule, value, callback) => {
      if (!Number.isFinite(value) || !Number.isInteger(value) || value < 1) {
        callback(`${label} must be a whole number of at least 1`);
        return;
      }
      callback();
    }
  }
];

const integerInputProps = {
  min: 1,
  precision: 0,
  style: { width: '100%' },
  onKeyDown: blockIntegerKeyDown,
  parser: parseIntegerInput
};

const decimalInputProps = {
  min: 0,
  step: 0.01,
  precision: 2,
  style: { width: '100%' },
  onKeyDown: blockDecimalKeyDown,
  parser: parseDecimalInput
};

const fieldLabel = (text, tooltip) => (
  <span>
    {text}{' '}
    <Tooltip title={tooltip}>
      <Icon type="question-circle-o" style={{ color: '#999', cursor: 'help' }} />
    </Tooltip>
  </span>
);

const FIELD_TOOLTIPS = {
  couponName: 'Internal description for this coupon. Used for admin reference only — not shown to customers at checkout.',
  code: 'Customer-facing code entered at checkout (e.g. SAVE10). Cannot be changed after creation.',
  discountType: 'Choose whether the discount is a percentage of the price or a fixed monetary amount.',
  percentOff: 'Percentage deducted from the eligible subscription price (0–100).',
  amountOff: 'Fixed amount deducted from the invoice total when this coupon is applied.',
  currency: 'Currency for fixed-amount discounts. Must match a currency supported by Stripe.',
  duration:
    'How long the discount applies per customer: Once (first invoice only), Repeating (for a set number of months), or Forever (every invoice).',
  durationInMonths: 'Number of months the discount repeats. Required when duration is set to Repeating.',
  couponMaxRedemptions: 'Campaign-wide limit: total uses across all customers. Blank = unlimited.',
  redeemBy: 'Last date and time the coupon can be redeemed. Leave blank if the coupon does not expire.',
  productIds: 'Restrict the coupon to specific e-Invoice subscription products. Leave blank to allow all products.',
  promotionCode: 'Customer-facing code entered at checkout. Can differ from the coupon ID. Defaults to the coupon code if left blank.',
  active: 'Whether this promotion code can be used at checkout. Inactive codes are rejected when customers try to redeem them.',
  expiresAt: 'Last date and time this promotion code can be redeemed. Leave blank if it does not expire.',
  promotionMaxRedemptions: 'Limit how many times this specific promotion code can be redeemed. Blank = unlimited.',
  customerId: 'Restrict redemption to a single Stripe customer (e.g. cus_...). Leave blank to allow any customer.',
  firstTimeTransaction: "When enabled, the code only applies to a customer's first successful payment.",
  minimumAmount: 'Minimum order total required before this promotion code can be applied.',
  minimumAmountCurrency: 'Currency for the minimum order amount.'
};

const formatRedemptions = (used, max) => {
  if (max) {
    return `${used || 0} / ${max}`;
  }
  return String(used || 0);
};

const formatProductsApplied = productsApplied => {
  if (!productsApplied || !productsApplied.length) {
    return '';
  }
  return productsApplied.map(p => p.stripeName || p.stripeProductId).join(', ');
};

const CouponDetails = ({ form, match, history, user }) => {
  const id = match.params.id;
  const isEdit = Boolean(id);
  const { getFieldDecorator, getFieldValue, setFieldsValue, validateFieldsAndScroll } = form;

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [discountType, setDiscountType] = useState('percent_off');
  const [voucher, setVoucher] = useState(null);

  const isActive = voucher && !voucher.pause && voucher.promotionCodeActive !== false;

  const loadVoucher = useCallback(() => {
    if (!isEdit || !id) {
      return Promise.resolve();
    }

    setLoading(true);
    return getVoucher(id)
      .then(data => {
        const loadedVoucher = data.voucher || data;
        const stripePromo = data.stripePromotionCode;
        const restrictions = (stripePromo && stripePromo.restrictions) || {};
        setVoucher(loadedVoucher);
        setDiscountType(loadedVoucher.type || 'percent_off');
        setFieldsValue({
          couponName: loadedVoucher.name,
          code: loadedVoucher.code || loadedVoucher.stripeCouponId,
          discountType: loadedVoucher.type || 'percent_off',
          percentOff: loadedVoucher.percentageOff,
          amountOff: loadedVoucher.amountOff,
          currency: loadedVoucher.currency || 'myr',
          duration: loadedVoucher.duration,
          durationInMonths: loadedVoucher.durationInMonths,
          couponMaxRedemptions: loadedVoucher.maxRedemptions,
          redeemBy: loadedVoucher.redeemBy ? moment(loadedVoucher.redeemBy) : null,
          productIds: (loadedVoucher.productsApplied || []).map(p => p.stripeProductId).filter(Boolean),
          promotionCode: loadedVoucher.promotionCode || (stripePromo && stripePromo.code),
          active: stripePromo ? stripePromo.active !== false : loadedVoucher.promotionCodeActive !== false,
          expiresAt: stripePromo && stripePromo.expires_at ? moment.unix(stripePromo.expires_at) : null,
          promotionMaxRedemptions:
            (stripePromo && stripePromo.max_redemptions) != null ? stripePromo.max_redemptions : loadedVoucher.promotionMaxRedemptions,
          customerId: (stripePromo && stripePromo.customer) || loadedVoucher.customerId,
          firstTimeTransaction: Boolean(restrictions.first_time_transaction),
          minimumAmount: restrictions.minimum_amount != null ? restrictions.minimum_amount / 100 : undefined,
          minimumAmountCurrency: restrictions.minimum_amount_currency || 'myr'
        });
      })
      .catch(() => message.error('Failed to load coupon'))
      .finally(() => setLoading(false));
  }, [id, isEdit, setFieldsValue]);

  useEffect(() => {
    if (user && !user.isAdmin) {
      history.push('/forbidden');
    }
  }, [user, history]);

  useEffect(() => {
    getVoucherProducts()
      .then(data => setProducts(Array.isArray(data) ? data : []))
      .catch(() => message.error('Failed to load products'));
  }, []);

  useEffect(() => {
    if (!isEdit || !user || !user.isAdmin) return;
    loadVoucher();
  }, [isEdit, user, loadVoucher]);

  const handleDeactivate = () => {
    Modal.confirm({
      title: 'Deactivate coupon?',
      content: 'This will deactivate the coupon in Stripe and mark it inactive locally.',
      okText: 'Deactivate',
      okType: 'danger',
      onOk: () =>
        deleteVoucher(id)
          .then(() => {
            message.success('Coupon deactivated');
            return loadVoucher();
          })
          .catch(() => message.error('Failed to deactivate coupon'))
    });
  };

  const handleReactivate = () => {
    Modal.confirm({
      title: 'Reactivate coupon?',
      content: 'This will reactivate the coupon in Stripe and mark it active locally.',
      okText: 'Reactivate',
      onOk: () =>
        reactivateVoucher(id)
          .then(() => {
            message.success('Coupon reactivated');
            return loadVoucher();
          })
          .catch(() => message.error('Failed to reactivate coupon'))
    });
  };

  const pageTitle = isEdit ? (voucher && (voucher.promotionCode || voucher.code || voucher.name)) || 'Coupon' : 'Create Coupon';

  const headerActions =
    isEdit && voucher ? (
      isActive ? (
        <Button type="danger" icon="stop" onClick={handleDeactivate}>
          Deactivate
        </Button>
      ) : (
        <Button type="primary" icon="check-circle" onClick={handleReactivate}>
          Reactivate
        </Button>
      )
    ) : null;

  if (!user || !user.isAdmin) {
    return null;
  }

  const handleSubmit = e => {
    e.preventDefault();
    validateFieldsAndScroll(async (err, values) => {
      if (err) return;

      setSubmitting(true);
      try {
        if (isEdit) {
          const payload = {
            coupon: {
              name: values.couponName,
              metadata: {}
            },
            promotionCode: {
              active: values.active,
              maxRedemptions: values.promotionMaxRedemptions || null
            }
          };
          await updateVoucher(id, payload);
          message.success('Coupon updated');
          await loadVoucher();
        } else {
          const couponCode = String(values.code).trim();
          const promoCode = values.promotionCode ? String(values.promotionCode).trim() : couponCode;
          const payload = {
            coupon: {
              id: couponCode,
              name: values.couponName,
              duration: values.duration,
              durationInMonths: values.duration === 'repeating' ? values.durationInMonths : undefined,
              maxRedemptions: values.couponMaxRedemptions || undefined,
              redeemBy: values.redeemBy ? values.redeemBy.toISOString() : undefined,
              productIds: values.productIds || [],
              metadata: {}
            },
            promotionCode: {
              code: promoCode,
              active: values.active !== false,
              expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
              maxRedemptions: values.promotionMaxRedemptions || undefined,
              customerId: values.customerId ? String(values.customerId).trim() : undefined,
              restrictions: {
                firstTimeTransaction: values.firstTimeTransaction,
                minimumAmount: values.minimumAmount,
                minimumAmountCurrency: values.minimumAmountCurrency
              }
            }
          };

          if (values.discountType === 'percent_off') {
            payload.coupon.percentOff = values.percentOff;
          } else {
            payload.coupon.amountOff = values.amountOff;
            payload.coupon.currency = values.currency;
          }

          const created = await createVoucher(payload);
          message.success('Coupon created');
          history.push(buildEInvoiceCouponDetailUri(String(created._id)));
        }
      } catch (error) {
        message.error((error && error.message) || 'Failed to save coupon');
      } finally {
        setSubmitting(false);
      }
    });
  };

  const currentDiscountType = getFieldValue('discountType') || discountType;
  const currentDuration = getFieldValue('duration');

  return (
    <StyledCard bodyStyle={{ padding: 0 }}>
      <HeaderSection>
        <CouponPageHeader backTo={buildEInvoiceCouponsUri()} backLabel="Back to Coupons" title={pageTitle} actions={headerActions} />
      </HeaderSection>
      <BodySection>
        <Spin spinning={loading}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
            message={
              isEdit
                ? 'Stripe does not allow changing discount amount, duration, code, or currency after creation. Only the coupon name and promotion code settings can be updated.'
                : 'Coupon discount amount, duration, and currency cannot be changed after creation. Only the coupon name and promotion code active status can be updated later.'
            }
          />

          <Form layout="vertical" onSubmit={handleSubmit}>
            <Divider orientation="left">Coupon Settings</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={fieldLabel('Code', FIELD_TOOLTIPS.code)}>
                  {getFieldDecorator('code', {
                    rules: [
                      { required: !isEdit, message: 'Code is required' },
                      {
                        validator: (rule, value, callback) => {
                          if (isEdit || !value || !String(value).trim()) {
                            if (!isEdit && (!value || !String(value).trim())) {
                              callback('Code is required');
                              return;
                            }
                            callback();
                            return;
                          }
                          const trimmed = String(value).trim();
                          if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
                            callback('Code can only contain letters, numbers, hyphens, and underscores');
                            return;
                          }
                          if (trimmed.length > 40) {
                            callback('Code must be 40 characters or fewer');
                            return;
                          }
                          callback();
                        }
                      }
                    ]
                  })(<Input placeholder="e.g. SAVE10" disabled={isEdit} maxLength={40} />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={fieldLabel('Name', FIELD_TOOLTIPS.couponName)}>
                  {getFieldDecorator('couponName', {
                    rules: [
                      { required: true, message: 'Name is required' },
                      { max: 255, message: 'Name must be 255 characters or fewer' },
                      {
                        validator: (rule, value, callback) => {
                          if (value && !String(value).trim()) {
                            callback('Name cannot be blank');
                            return;
                          }
                          callback();
                        }
                      }
                    ]
                  })(<Input placeholder="Internal description" maxLength={255} />)}
                </Form.Item>
              </Col>
            </Row>

            {isEdit && (
              <Row gutter={16} style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <Form.Item label="Status">{isActive ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>}</Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Coupon Redemptions">
                    <Input disabled value={formatRedemptions(voucher && voucher.timesRedeemed, voucher && voucher.maxRedemptions)} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Promo Redemptions">
                    <Input
                      disabled
                      value={formatRedemptions(voucher && voucher.promotionTimesRedeemed, voucher && voucher.promotionMaxRedemptions)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={fieldLabel('Discount Type', FIELD_TOOLTIPS.discountType)}>
                  {getFieldDecorator('discountType', {
                    initialValue: 'percent_off',
                    rules: [{ required: !isEdit, message: 'Discount type is required' }]
                  })(
                    <Select disabled={isEdit} onChange={value => setDiscountType(value)}>
                      <Option value="percent_off">Percent off</Option>
                      <Option value="amount_off">Amount off</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
              {currentDiscountType === 'percent_off' ? (
                <Col span={8}>
                  <Form.Item label={fieldLabel('Percent Off', FIELD_TOOLTIPS.percentOff)}>
                    {getFieldDecorator('percentOff', {
                      rules: [
                        { required: !isEdit, message: 'Percent off is required' },
                        {
                          validator: (rule, value, callback) => {
                            if (isEdit || value == null || value === '') {
                              callback();
                              return;
                            }
                            if (!Number.isFinite(value) || value < 0 || value > 100) {
                              callback('Percent off must be between 0 and 100');
                              return;
                            }
                            callback();
                          }
                        }
                      ]
                    })(
                      <InputNumber
                        min={0}
                        max={100}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                        disabled={isEdit}
                        onKeyDown={blockDecimalKeyDown}
                        parser={parseDecimalInput}
                      />
                    )}
                  </Form.Item>
                </Col>
              ) : (
                <>
                  <Col span={8}>
                    <Form.Item label={fieldLabel('Amount Off', FIELD_TOOLTIPS.amountOff)}>
                      {getFieldDecorator('amountOff', {
                        rules: [{ required: !isEdit, message: 'Amount off is required' }, ...optionalPositiveDecimalRules('Amount off')]
                      })(<InputNumber {...decimalInputProps} disabled={isEdit} />)}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={fieldLabel('Currency', FIELD_TOOLTIPS.currency)}>
                      {getFieldDecorator('currency', {
                        initialValue: 'myr',
                        rules: [{ required: !isEdit, message: 'Currency is required' }]
                      })(
                        <Select disabled={isEdit}>
                          <Option value="myr">MYR</Option>
                          <Option value="usd">USD</Option>
                          <Option value="sgd">SGD</Option>
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={fieldLabel('Duration', FIELD_TOOLTIPS.duration)}>
                  {getFieldDecorator('duration', {
                    initialValue: 'once',
                    rules: [{ required: !isEdit, message: 'Duration is required' }]
                  })(
                    <Select disabled={isEdit}>
                      <Option value="once">Once</Option>
                      <Option value="repeating">Repeating</Option>
                      <Option value="forever">Forever</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
              {(currentDuration === 'repeating' || (isEdit && voucher && voucher.duration === 'repeating')) && (
                <Col span={8}>
                  <Form.Item label={fieldLabel('Duration in Months', FIELD_TOOLTIPS.durationInMonths)}>
                    {getFieldDecorator('durationInMonths', {
                      rules: isEdit ? [] : requiredPositiveIntegerRules('Duration in months')
                    })(<InputNumber {...integerInputProps} disabled={isEdit} />)}
                  </Form.Item>
                </Col>
              )}
              <Col span={8}>
                <Form.Item label={fieldLabel('Max Redemptions (coupon)', FIELD_TOOLTIPS.couponMaxRedemptions)}>
                  {getFieldDecorator('couponMaxRedemptions', {
                    rules: optionalPositiveIntegerRules('Max redemptions (coupon)')
                  })(<InputNumber {...integerInputProps} disabled={isEdit} />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={fieldLabel('Redeem By', FIELD_TOOLTIPS.redeemBy)}>
                  {getFieldDecorator('redeemBy')(<DatePicker showTime style={{ width: '100%' }} disabled={isEdit} />)}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={fieldLabel('Applies To Products', FIELD_TOOLTIPS.productIds)}>
                  {isEdit ? (
                    <Input disabled value={formatProductsApplied(voucher && voucher.productsApplied) || '—'} />
                  ) : (
                    getFieldDecorator('productIds', { initialValue: [] })(
                      <Select mode="multiple" allowClear placeholder="Select products (optional)">
                        {products.map(product => (
                          <Option key={product.productId} value={product.productId}>
                            {product.name} ({product.productId})
                          </Option>
                        ))}
                      </Select>
                    )
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Promotion Code Settings</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={fieldLabel('Promo Code', FIELD_TOOLTIPS.promotionCode)}>
                  {getFieldDecorator('promotionCode', {
                    rules: isEdit
                      ? []
                      : [
                          {
                            validator: (rule, value, callback) => {
                              const couponCode = getFieldValue('code');
                              const trimmed = value ? String(value).trim() : couponCode ? String(couponCode).trim() : '';
                              if (!trimmed) {
                                callback('Promo code is required');
                                return;
                              }
                              callback();
                            }
                          }
                        ]
                  })(<Input placeholder="Customer-facing code" disabled={isEdit} maxLength={500} />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={fieldLabel('Active', FIELD_TOOLTIPS.active)}>
                  {getFieldDecorator('active', {
                    valuePropName: 'checked',
                    initialValue: true
                  })(<Switch />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={fieldLabel('Expires At', FIELD_TOOLTIPS.expiresAt)}>
                  {getFieldDecorator('expiresAt')(<DatePicker showTime style={{ width: '100%' }} disabled={isEdit} />)}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={fieldLabel('Max Redemptions (promo code)', FIELD_TOOLTIPS.promotionMaxRedemptions)}>
                  {getFieldDecorator('promotionMaxRedemptions', {
                    rules: optionalPositiveIntegerRules('Max redemptions (promo code)')
                  })(<InputNumber {...integerInputProps} />)}
                </Form.Item>
              </Col>
              {!isEdit && (
                <Col span={8}>
                  <Form.Item label={fieldLabel('Customer ID (optional)', FIELD_TOOLTIPS.customerId)}>
                    {getFieldDecorator('customerId', {
                      rules: [
                        {
                          validator: (rule, value, callback) => {
                            if (!value || !String(value).trim()) {
                              callback();
                              return;
                            }
                            if (!/^cus_[A-Za-z0-9]+$/.test(String(value).trim())) {
                              callback('Customer ID must be a valid Stripe customer ID (e.g. cus_...)');
                              return;
                            }
                            callback();
                          }
                        }
                      ]
                    })(<Input placeholder="Stripe customer ID" />)}
                  </Form.Item>
                </Col>
              )}
              <Col span={8}>
                <Form.Item label={fieldLabel('First-time Transaction Only', FIELD_TOOLTIPS.firstTimeTransaction)}>
                  {getFieldDecorator('firstTimeTransaction', {
                    valuePropName: 'checked',
                    initialValue: false
                  })(<Switch disabled={isEdit} />)}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label={fieldLabel('Minimum Amount', FIELD_TOOLTIPS.minimumAmount)}>
                  {getFieldDecorator('minimumAmount', {
                    rules: optionalPositiveDecimalRules('Minimum amount')
                  })(<InputNumber {...decimalInputProps} disabled={isEdit} />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={fieldLabel('Minimum Amount Currency', FIELD_TOOLTIPS.minimumAmountCurrency)}>
                  {getFieldDecorator('minimumAmountCurrency', { initialValue: 'myr' })(
                    <Select disabled={isEdit}>
                      <Option value="myr">MYR</Option>
                      <Option value="usd">USD</Option>
                      <Option value="sgd">SGD</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
              {isEdit && voucher && voucher.customerId && (
                <Col span={8}>
                  <Form.Item label={fieldLabel('Customer ID', FIELD_TOOLTIPS.customerId)}>
                    <Input disabled value={voucher.customerId} />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? 'Save Changes' : 'Create Coupon'}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </BodySection>
    </StyledCard>
  );
};

export default withAppContext(withRouter(Form.create()(CouponDetails)));
